import { ArrowSmRightIcon } from '@heroicons/react/solid'
import { useNavigate } from 'react-router-dom'
import {
  Anchor,
  Form,
  Paragraph,
  StickyFooter,
  TextareaField,
} from 'src/components'
import EmailHelpBox from 'src/components/Layout/EmailHelpBox'
import CustomButton from 'src/components/Shared/UIComponents/Button'
import InputField from 'src/components/Shared/UIComponents/InputField'
import { getBackendByRegion } from 'src/regions'
import {
  savedIdPid,
  savedPrincipalArn,
  savedRoleArn,
  savedSpid,
} from '../../storage'
import { useEditOrganization } from 'src/routes/Onboarding/queries'
import { useResyncSubscriptionProvisioning } from '../../queries'
import { savedTokens } from 'src/auth'
import { toast } from 'sonner'
import { permissionPolicy } from '../utils'
import TooltipLabel from 'src/components/Shared/UIComponents/TooltipLabel'

type Props = {
  organization: string
  username: string
  connectUrl: string
  instanceId: string
  accountId: string
  region: string
  arn: string
}

export const GoogleWorkspaceContent = (props: Props) => {
  const navigate = useNavigate()
  const editOrganization = useEditOrganization()
  const resyncSubscription = useResyncSubscriptionProvisioning()

  const [principalArn, setPrincipalArn] = savedPrincipalArn.useState('')
  const [roleArn, setRoleArn] = savedRoleArn.useState('')
  const [idpid, setIdPid] = savedIdPid.useState('')
  const [spid, setSpid] = savedSpid.useState('')

  const link = new URL(
    `/token/sso?org=${props.organization}`,
    getBackendByRegion(savedTokens.get()?.region),
  ).href

  return (
    <div className="flex flex-col gap-6 mt-8 text-secondary">
      <div className="flex flex-col gap-2">
        <Paragraph className="font-bold underline">Step 1</Paragraph>
        <Paragraph>
          To connect with Google Workspace SSO, follow the exact same steps as
          in this{' '}
          <Anchor
            href="https://catalog.us-east-1.prod.workshops.aws/workshops/33e6d0e7-f927-4531-abb1-f28a86ba0872/en-US/4-sso-amazonconnect-google-workspace"
            target="_blank"
            className="underline"
          >
            installation guide
          </Anchor>
          .
        </Paragraph>
      </div>

      <div className="flex flex-col gap-2">
        <Paragraph className="font-bold underline">Step 2</Paragraph>
        <Paragraph>
          During step 1, you have created the following resources in AWS IAM:
        </Paragraph>
        <ol className="list-decimal pl-5 mb-4">
          <li>IAM Identity Provider</li>
          <li>IAM Role</li>
        </ol>
        <Paragraph>
          a) Please enter the ARN of the these resources below
        </Paragraph>
      </div>

      <Form
        className="gap-12"
        onSubmit={async (e) => {
          e.preventDefault()

          if (
            principalArn &&
            props.region &&
            roleArn &&
            props.connectUrl &&
            props.instanceId &&
            props.accountId &&
            props.arn &&
            idpid &&
            spid
          ) {
            await editOrganization.mutateAsync({
              cognito_url: '',
              userPoolAppClientId: '',
              userPoolClientSecret: '',
              userPoolId: '',
              userPoolRegion: '',
              userPoolTokenType: '',
              aws_connect_saml_url: props.connectUrl,
              iam_organization_admin_accessKey: '',
              iam_organization_admin_region: '', //didn't exist
              iam_organization_usergroup: '', //didn't exist
              redirect_uri: '', //didn't exist
              aws_connect_type: 'SSO',
              iam_organization_admin_accessSecret: '',
              iam_organization_policy_arn: '',
              code: props.organization,
              organization_connect_account_id: props.accountId,
              organization_connect_instance_id: props.instanceId,
              organization_connect_arn: props.arn,
              setupStep: 'finished',
              username: props.username,
              organization: props.organization,
              region: props.region,
              sso: {
                loginUrl: `https://accounts.google.com/o/saml2/initsso?idpid=${idpid}&spid=${spid}`,
                principalArn: principalArn,
                region: props.region,
                roleArn: roleArn,
                type: 'googleworkspace',
              },
              application: {
                chatregion: props.region,
              },
            })

            await resyncSubscription.mutateAsync({
              organization: props.organization,
            })

            navigate('../finish')
          } else {
            toast.error(`ARN is not valid.`)
          }
        }}
      >
        <div className="col-span-full grid gap-10">
          <div className="col-span-full grid gap-4">
            <InputField
              label="Identity provider ARN"
              placeholder="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
              value={principalArn}
              setValue={(value) => {
                setPrincipalArn(value.trim())
              }}
              title="arn:aws:iam::ACCOUNT_ID:saml-provider/PROVIDER_ID"
              pattern="arn:aws:iam::\d{12}:saml-provider\/[A-Za-z0-9\-_]+"
              className="col-span-full"
              tooltip={
                <p>
                  Navigate to{' '}
                  <b>
                    AWS Management console -&gt; IAM -&gt; Identity Providers
                    -&gt; IdentityCenter-SSO -&gt; ARN
                  </b>
                </p>
              }
            />
            <InputField
              label="Role ARN"
              placeholder="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
              value={roleArn}
              setValue={(value) => {
                setRoleArn(value.trim())
              }}
              title="arn:aws:iam::ACCOUNT_ID:role/ROLE_ID"
              pattern="arn:aws:iam::\d{12}:role\/[A-Za-z0-9\-_]+"
              className="col-span-full"
              tooltip={
                <p>
                  Navigate to{' '}
                  <b>
                    AWS Management console -&gt; IAM -&gt; Roles -&gt; Click
                    “IdentityCenter_SAML_Role” role -&gt; ARN
                  </b>
                </p>
              }
            />
          </div>

          <div className="flex flex-col gap-4">
            <TooltipLabel
              label={
                <Paragraph className="font-normal text-[16px]">
                  b) Please change the condition in your permission policy to
                  the below:
                </Paragraph>
              }
              tooltip={
                <div className="flex flex-col gap-4">
                  <Paragraph className="font-bold">
                    AWS Management Console -&gt; IAM &gt; Roles &gt; ROLE_NAME
                    -&gt; Trust relationships
                  </Paragraph>
                  <img
                    alt="Permission policy"
                    width={600}
                    src={require('src/images/permission_policy.png')}
                  />
                </div>
              }
            />
            <div className="flex flex-row gap-4 items-center">
              <TextareaField
                classNameInput="h-32 overflow-hidden text-lg"
                value={permissionPolicy}
                disabled
              />
              <CustomButton
                type="button"
                label="copy"
                onClick={() => {
                  navigator.clipboard.writeText(permissionPolicy)
                  toast.success(
                    `Permission policy has been copied to your clipboard.`,
                  )
                }}
              />
            </div>
          </div>

          <div>
            <Paragraph className="font-bold underline">Step 3</Paragraph>
            <div className="flex flex-col gap-6">
              <Paragraph>
                During step 1, you created an Amazon Web Services SAML app in
                Google Workspace. To ensure you are redirected to Mobile Connect
                after authentication, some slight changes are needed. More
                specifically:
              </Paragraph>
              <ul className="text-secondary list-disc list-outside pl-5 mb-4">
                <li>
                  <TooltipLabel
                    label={
                      <Paragraph>
                        Change the <b>“ACS URL”</b> to the below provided URL so
                        that it points to Mobile Connect.
                      </Paragraph>
                    }
                    tooltip={
                      <img
                        alt="ACS URL Google Workspace"
                        width={700}
                        src={require('src/images/acsurl_google.png')}
                      />
                    }
                  />
                </li>
                <li>
                  Do <u>not</u> change the <b>“Entity ID”</b>.
                </li>
                <li>
                  The <b>“start URL”</b> field can remain empty.
                </li>
              </ul>
              <Paragraph>
                Navigate to{' '}
                <b>
                  Google Admin Console -&gt; Apps &gt; Web and Mobile Apps &gt;
                  Amazon Web Services -&gt; Service Provider Details -&gt; ACS
                  URL
                </b>{' '}
                and paste the below URL.
              </Paragraph>
              <div className="flex items-end col-span-full gap-4">
                <InputField
                  label="ACS URL"
                  value={link}
                  disabled
                  setValue={() => {}}
                  className="bg-foreground text-secondary"
                />
                <CustomButton
                  type="button"
                  label="copy"
                  onClick={() => {
                    navigator.clipboard.writeText(link)
                    toast.success(
                      `Setup link has been copied to your clipboard.`,
                    )
                  }}
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-6">
            <Paragraph className="font-bold underline">Step 4</Paragraph>
            <div className="flex flex-col gap-2">
              <Paragraph>
                As a fourth step, we need to construct your SSO Entry Point URL.
                the SSO Entry Point URL is a URL that your users will be
                directed to when they visit your account page. Unlike most
                identity providers, Google does not provide an easily accessible
                URL when creating a custom SAML application, you must construct
                it. In Google Workspace, this SSO Entry Point URL consists of
                two elements:{' '}
              </Paragraph>
              <ul className="text-secondary list-disc list-outside pl-5 mb-4">
                <li>idpid</li>
                <li>spid</li>
              </ul>
              <Paragraph>
                Please follow the below steps to provide the idpid and the spid.
                Based on this input, we will automatically construct your SSO
                Entry Point URL.
              </Paragraph>
            </div>

            <div className="flex flex-col gap-2">
              <Paragraph className="font-bold text-md">a) idpid</Paragraph>
              <Paragraph>
                The idpid can be found by navigating to{' '}
                <b>
                  Google Admin Console -&gt; Apps &gt; Web and Mobile Apps &gt;
                  Amazon Web Services -&gt; Service Provider Details
                </b>{' '}
                and click <b>Download IdP Metadata</b>. The idpid is the
                aplhanumeric text located after “idpid=” in the google SSO URL.
                Please provide only the alphanumeric code below.
              </Paragraph>
              <div className="flex flex-col gap-2 mt-4">
                <InputField
                  label="idpid"
                  placeholder="2222222222"
                  value={idpid}
                  setValue={(value) =>
                    value.trim().length <= 30 && setIdPid(value.trim())
                  }
                  className="bg-foreground text-secondary w-64"
                  tooltip={
                    <img
                      width={400}
                      alt="idpid"
                      src={require('src/images/idpid.png')}
                    />
                  }
                />
                <Paragraph className="text-sm text-wrap break-all">
                  Example: https://accounts.google.com/o/saml2?idpid=
                  <b className="text-accent-foreground">
                    {idpid === '' ? '2222222222' : idpid}
                  </b>
                </Paragraph>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Paragraph className="font-bold text-md">b) spid</Paragraph>
              <Paragraph>
                The spid can be found by navigating to{' '}
                <b>
                  Google Admin Console -&gt; Apps &gt; Web and Mobile Apps &gt;
                  Amazon Web Services
                </b>
                . The spid can then be located in the URL of the custom SAML
                app's overview page, after “/apps/saml”.{' '}
              </Paragraph>
              <div className="flex flex-col gap-2 mt-4">
                <InputField
                  label="spid"
                  placeholder="1111111111"
                  value={spid}
                  setValue={(value) =>
                    value.trim().length <= 30 && setSpid(value.trim())
                  }
                  className="bg-foreground text-secondary w-64"
                  tooltip={
                    <img
                      width={600}
                      alt="spid"
                      src={require('src/images/spid.png')}
                    />
                  }
                />
                <Paragraph className="text-sm text-wrap break-all">
                  Example: https://admin.google.com/ac/apps/saml/
                  <b className="text-accent-foreground">
                    {spid === '' ? '1111111111' : spid}
                  </b>
                </Paragraph>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Paragraph className="font-bold underline">Step 5</Paragraph>
              <Paragraph>
                As a last step, please make sure that the you assign the newly
                created role in Google Workspace to the users that you would
                like to give access to Mobile Connect.
              </Paragraph>
            </div>
          </div>
        </div>
        <StickyFooter>
          <EmailHelpBox className="flex-1" />
          <CustomButton
            type="submit"
            label="next"
            isLoading={
              editOrganization.isLoading || resyncSubscription.isLoading
            }
            icon={<ArrowSmRightIcon />}
            iconPosition="right"
          />
        </StickyFooter>
      </Form>
    </div>
  )
}
